/* eslint-disable */
// @ts-nocheck
import { CardCvcElement, CardExpiryElement, CardNumberElement } from '@stripe/react-stripe-js';
import {
  StripeCardCvcElement,
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElement,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElement,
  StripeCardNumberElementChangeEvent,
} from '@stripe/stripe-js';
import cx from 'classnames';
import React, { MutableRefObject, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { FieldLabel, HelperText } from 'components/Forms';
import { ConnectedFormInputProps } from 'types/form-field';
import { StateAbbrev } from 'utils/states';
import { cardClasses, cvcClasses, expiryClasses, inputStyle } from './styles';

export type Props = {
  /**
   * State that the current user resides in. This is required in order to
   * select the appropriate Stripe publishable key for regulatory and
   * compliance reasons.
   */
  state: StateAbbrev;
} & ConnectedFormInputProps<HTMLInputElement>;

type ChangeEvent =
  | StripeCardNumberElementChangeEvent
  | StripeCardExpiryElementChangeEvent
  | StripeCardCvcElementChangeEvent;

type ElementType = 'cardNumber' | 'cardExpiry' | 'cardCvc';
type ErrorState = { [key in ElementType]: string | null };
type CompletionState = { [key in ElementType]: boolean };

/**
 * This component handles connecting the stripe elements card fields/state
 * to the react-hook-form state provided by <Form /> and <FormField />.
 */
export const CreditCardInput = React.forwardRef(function CreditCardInputWithRef(
  { setError, setValue, clearValue, ...inputProps }: Omit<Props, 'state'>,
  ref?: React.ForwardedRef<{ focus: () => void }>,
): JSX.Element | null {
  const cardNumberRef: MutableRefObject<StripeCardNumberElement | null> = useRef(null);

  const [errors, setErrors] = useState<ErrorState>({
    cardNumber: null,
    cardExpiry: null,
    cardCvc: null,
  });

  const errorsString = Object.values(errors).filter(Boolean).join('\n');

  // @NOTE(shawk): allow react-hook-form to send focus to the card number input
  useImperativeHandle(ref, () => ({
    focus: () => cardNumberRef.current?.focus(),
  }));

  useEffect(() => {
    setError?.(errorsString);
  }, [setError, errorsString]);

  const handleCardComplete = useCallback(
    complete => {
      if (!errorsString) {
        setValue?.(complete);
      }
    },
    [errorsString, setValue],
  );

  const handleFieldError = useCallback((field, message) => setErrors(e => ({ ...e, [field]: message })), [setErrors]);
  const handleCardNumberReady = useCallback(element => {
    cardNumberRef.current = element;
  }, []);

  return (
    <CreditCardInputUi
      {...inputProps}
      onFieldError={handleFieldError}
      onCardComplete={handleCardComplete}
      onCardNumberReady={handleCardNumberReady}
    />
  );
});

type UiProps = {
  onFieldError: (field: ElementType, error: string | null) => void;
  onCardNumberReady: (element: StripeCardNumberElement) => void;
  onCardComplete: (complete: boolean) => void;
} & Omit<ConnectedFormInputProps<HTMLInputElement>, 'setError' | 'setValue' | 'clearValue'>;

export const CreditCardInputUi = ({
  name,
  label,
  error,
  onFieldError,
  onCardNumberReady,
  onCardComplete,
  disabled,
  className,
  ...inputProps
}: UiProps): JSX.Element => {
  const expiryRef: MutableRefObject<StripeCardExpiryElement | null> = useRef(null);
  const cvcRef: MutableRefObject<StripeCardCvcElement | null> = useRef(null);

  const [completion, setCompletion] = useState<CompletionState>({
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false,
  });

  useEffect(() => {
    onCardComplete(Object.values(completion).every(v => !!v));
  }, [onCardComplete, completion]);

  const handleChange = (e: ChangeEvent) => {
    onFieldError(e.elementType, e.error?.message || null);
    setCompletion(c => ({ ...c, [e.elementType]: e.complete }));
    maybeFocusNextField(e);
  };

  const maybeFocusNextField = (e: ChangeEvent) => {
    if (!e.complete) {
      return;
    }

    switch (e.elementType) {
      case 'cardNumber':
        expiryRef.current?.focus();
        break;
      case 'cardExpiry':
        cvcRef.current?.focus();
        break;
    }
  };

  return (
    <div className={className}>
      <FieldLabel id="hidden-card-input">{label}</FieldLabel>

      <input name={name} type="hidden" id="hidden-card-input" {...inputProps} />

      <div className="relative rounded-md flex flex-wrap">
        <CardNumberElement
          className="w-full"
          options={{ classes: cardClasses(disabled, !!error), style: inputStyle, disabled }}
          onChange={handleChange}
          onReady={onCardNumberReady}
        />

        <CardExpiryElement
          className="w-1/2"
          options={{ classes: expiryClasses(disabled, !!error), style: inputStyle, disabled }}
          onChange={handleChange}
          onReady={element => {
            expiryRef.current = element;
          }}
        />

        <CardCvcElement
          className="w-1/2"
          options={{ classes: cvcClasses(disabled, !!error), style: inputStyle, disabled }}
          onChange={handleChange}
          onReady={element => {
            cvcRef.current = element;
          }}
        />

        <div
          className={cx(
            'absolute inset-0 outline-1 outline outline-offset-[-1px] rounded-md outline-error-900 pointer-events-none z-10',
            { 'opacity-0': !error },
          )}
        />
      </div>

      {error && (
        <HelperText error>
          {/* @NOTE(shawk): this error may have new lines that we want to respect */}
          <pre style={{ fontFamily: 'inherit' }}>{error}</pre>
        </HelperText>
      )}
    </div>
  );
};
